// 3rd party
import React, { act, ReactElement } from 'react';
import styled, { useTheme } from 'styled-components';
import { useWizard } from 'react-use-wizard';
import { useTranslation } from 'react-i18next';
// Components
import { Banner, Typography } from 'components';
import MultiStepIndicator from 'common/components/MultiStepIndicator';
import { IcoBilling } from 'icons/indexed-icons/IcoBilling';
import CartDownload from './CartDownload';
import Quote from 'icons/indexed-icons/Quote';
import { BannerBox } from './CartWizard.elements';
import Duplicate from 'icons/indexed-icons/Duplicate';
import { CartPriceType } from './helper';
import Estimation from 'icons/indexed-icons/Estimation';

// Styling
const Header = styled.div`
  display: flex;
  flex-direction: column;
  // padding: 20px;
  width: 100%;
  padding: 1rem 2rem 0;
  border-top-left-radius: 0.625rem;
  border-top-right-radius: 0.625rem;

  h2 {
    font-weight: bold;
    line-height: 1.5625rem;
    margin-bottom: 0.625rem;
  }
`;
interface StepProps {
  header: string;
  description: string;
}
type BannerState = {
  cartPriceType?: CartPriceType;
  selectedCartPriceType?: CartPriceType;
  pricedQuoteRequestId?: string;
  nonePricedQuoteRequestId?: string;
};
interface Props {
  stepHeaders: StepProps[];
  isCartsEmpty?: boolean;
  onDownloadDraftPO?: () => void;
  downloadDraftPDFLoader?: boolean;
  cartBannerState?: BannerState;
  twoWaySyncFeature?: boolean;
  cartItemsType?: CartPriceType;
}
const CartHeader = ({
  stepHeaders,
  isCartsEmpty,
  onDownloadDraftPO,
  downloadDraftPDFLoader,
  twoWaySyncFeature,
  cartBannerState,
  cartItemsType
}: Props): ReactElement => {
  const theme = useTheme();
  const { activeStep, goToStep, isLastStep } = useWizard();
  const { t } = useTranslation(['fpns']);
  const handlePress = (index: number) => {
    // handle press should be disable on last step.
    if (index < activeStep && activeStep !== 2) {
      goToStep(index);
    }
  };

  const steps = [
    { name: 'step_verify_cart', icon: Quote, stepIndex: 0 },
    { name: 'step_shipping_billing', icon: IcoBilling, stepIndex: 1 },
    {
      name: 'make_a_selection',
      icon: Duplicate,
      isTwowaySync: twoWaySyncFeature,
      cartPriceType: cartBannerState?.cartPriceType,
      stepIndex: 2
    },
    { name: 'final_estimate', icon: Estimation, stepIndex: 3 }
  ];

  const getBannerTextForNonePrice = (cartBannerState?: BannerState, activeStep?: number) => {
    if (activeStep === 0) {
      return cartBannerState?.cartPriceType === 'mix'
        ? t('subtotal_exclude_none_price')
        : cartBannerState?.cartPriceType === 'none-price'
        ? t('quote_message_for_none_price')
        : '';
    } else if ((activeStep === 3 || isLastStep) && twoWaySyncFeature) {
      return cartBannerState?.selectedCartPriceType === 'mix'
        ? t('subtotal_exclude_none_price')
        : t('quote_message_for_none_price');
    }
  };

  const getBannerText = (cartBannerState?: BannerState, activeStep?: number) => {
    if (cartBannerState?.selectedCartPriceType === 'price' && activeStep === 3 && twoWaySyncFeature)
      return '';
    if (cartBannerState?.cartPriceType !== 'price') {
      return getBannerTextForNonePrice(cartBannerState, activeStep);
    }
    return '';
  };

  const getBannerTextBySelection = (cartBannerState?: BannerState, activeStep?: number) => {
    if (
      (cartBannerState?.selectedCartPriceType === 'none-price' ||
        cartBannerState?.selectedCartPriceType === 'mix') &&
      activeStep === 0 &&
      twoWaySyncFeature
    )
      return t('quote_message_for_none_price');
    return '';
  };
  const bannerText = getBannerText(cartBannerState, activeStep);
  const bannerTextBySelection = getBannerTextBySelection(cartBannerState, activeStep);

  const showSuccessBanner = () => {
    return (
      cartBannerState?.nonePricedQuoteRequestId &&
      twoWaySyncFeature &&
      activeStep === 3 &&
      cartBannerState.selectedCartPriceType === 'mix'
    );
  };

  return (
    <>
      <Header>
        <Typography variant="h2">
          {t(stepHeaders[activeStep].header, { ns: ['fpns', 'common'] })}
        </Typography>
        <Typography color={theme.colors.lightGrey8} variant="body2" mb="0.0625rem">
          {t(stepHeaders[activeStep].description, { ns: ['fpns', 'common'] })}
        </Typography>
      </Header>
      <MultiStepIndicator
        handlePress={handlePress}
        steps={steps}
        currentStep={activeStep}
        isEmpty={isCartsEmpty}
        isTwowaySync={twoWaySyncFeature}
        cartPriceType={cartBannerState?.cartPriceType}
        cartItemsType={cartItemsType}
      />
      {showSuccessBanner() && (
        <BannerBox>
          <Banner
            message={`Success! Your quote request for products with no price has been submitted. Check your email for confirmation. Quote #${cartBannerState?.nonePricedQuoteRequestId}`}
            type="success"
          />
        </BannerBox>
      )}
      {bannerText && !isCartsEmpty && (
        <BannerBox>
          <Banner message={bannerText} type="info" />
        </BannerBox>
      )}
      {bannerTextBySelection &&
        !isCartsEmpty &&
        cartBannerState?.cartPriceType !== 'none-price' && (
          <BannerBox>
            <Banner message={bannerTextBySelection} type="info" />
          </BannerBox>
        )}
      {activeStep < (twoWaySyncFeature ? 4 : 2) && (
        <CartDownload
          onDownloadDraftPO={!isCartsEmpty ? onDownloadDraftPO : undefined}
          downloadDraftPDFLoader={!isCartsEmpty ? downloadDraftPDFLoader : undefined}
          selectedCartPriceType={cartBannerState?.selectedCartPriceType}
        />
      )}
    </>
  );
};

export default CartHeader;
